import { createCloudRunAxios } from './axios';

const remiApi = createCloudRunAxios('remi-api');

/**
 * Step names match the `step` field returned by the orchestrator (StepResult.step).
 * These are action labels, NOT the Python module names. Keep this list in the
 * same order the backend runs the steps so the drawer renders top-to-bottom.
 */
export type DeactivationStepName =
  | 'cancel_scheduled_calls'
  | 'cancel_scheduled_emails'
  | 'disable_email_features'
  | 'takedown_idx_domain'
  | 'delete_sendgrid_sender'
  | 'revoke_user_access';

export const DEACTIVATION_STEP_ORDER: DeactivationStepName[] = [
  'cancel_scheduled_calls',
  'cancel_scheduled_emails',
  'disable_email_features',
  'takedown_idx_domain',
  'delete_sendgrid_sender',
  'revoke_user_access',
];

export type DeactivationStepStatus = 'success' | 'skipped' | 'error';

export type DeactivationStatus =
  | 'queued'
  | 'processing'
  | 'completed'
  | 'completed_with_errors'
  | 'failed';

export interface DeactivationStepResult {
  step: string;
  status: DeactivationStepStatus;
  counts: Record<string, number>;
  errors: string[];
  duration_ms: number;
}

export interface DeactivationStatusResponse {
  request_id: string;
  organization_id: string;
  status: DeactivationStatus;
  triggered_by_uid: string | null;
  started_at: string | null;
  completed_at: string | null;
  /** Map of step name → step result. Populated once the orchestrator finishes. */
  steps: Record<string, DeactivationStepResult> | null;
  /** Top-level error only set when publish failed before processing started. */
  error: string | null;
}

export interface DeactivateAcceptedResponse {
  success: true;
  queued: true;
  request_id: string;
  organization_id: string;
  deactivated_at: string;
  endpoint: string;
}

export interface ActivateResponse {
  success: boolean;
  message: string;
  results: {
    organization: {
      activeClient: boolean;
      organization_updated: boolean;
    };
  };
}

/** Activate an org — flips `active_client = true`. Returns synchronously. */
export const activateOrganization = async (orgId: string): Promise<ActivateResponse> => {
  const response = await remiApi.post('/api/v1/integrations/active-client', {
    orgId,
    activeClient: true,
  });
  return response.data;
};

/**
 * Deactivate an org — flips `active_client = false` inline, then runs the
 * cleanup pipeline (flows / emails / IDX / SendGrid / users) in the background.
 * Returns 202 with a `request_id` you can poll via `getDeactivationStatus`.
 */
export const deactivateOrganization = async (
  orgId: string
): Promise<DeactivateAcceptedResponse> => {
  const response = await remiApi.post('/api/v1/integrations/active-client', {
    orgId,
    activeClient: false,
  });
  return response.data;
};

/** Poll the status of an in-flight deactivation by request_id. */
export const getDeactivationStatus = async (
  requestId: string
): Promise<DeactivationStatusResponse> => {
  const response = await remiApi.get(
    `/api/v1/integrations/active-client/${requestId}`
  );
  return response.data;
};

export const isTerminalDeactivationStatus = (status: DeactivationStatus): boolean =>
  status === 'completed' || status === 'completed_with_errors' || status === 'failed';
